import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { updateBookingStatus, updateDeliveryStatus, updateReturnStatus, adjustBookingBilling } from '../api';
import ConfirmModal from './ConfirmModal';

// v3_fix_issues #12 - the rental owner's booking-management actions, extracted so the website
// admin gets exactly the same set (the backend already authorises admin on every one of these
// routes). Used by OwnerDashboard, AdminDashboard (bookings list) and OrderDetailsModal.
//
// Pure status transitions (accept/decline, advance delivery, schedule/confirm return, report
// damage) are self-contained: they call the api and then `onChanged()` to refetch. The
// modal-based actions (assign delivery, cancel, view cancellation, record payment, mark refund
// sent) are delegated to the parent via callback props - a button only renders when its callback
// is supplied, so this stays drop-in for callers that don't wire every modal.

// Per-vertical wording.
const DELIVERY_ACTIONS = {
  PENDING: { label: 'Mark Prepared', next: 'PICKED_UP' },
  PICKED_UP: { label: 'Mark Out for Delivery', next: 'IN_TRANSIT' },
  IN_TRANSIT: { label: 'Mark Handed Over', next: 'DELIVERED' }
};
const RETURN_NOUN = 'Return';
const CONFIRM_RETURN_PROMPT = 'Confirm this item has been received back in good condition?';

function needsRefund(booking) {
  return booking.depositAmount > 0 && booking.depositStatus === 'REFUND_PENDING';
}

export default function BookingActions({
  booking, onChanged, isAdmin = false,
  onViewProduct, onRecordPayment, onMarkRefundSent, onViewCancellation, onAssignDelivery, onCancel
}) {
  const [busy, setBusy] = useState(false);
  // v4_fix_issues #5 - extend a booking's rental period; the rental is recomputed pro-rata.
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustEnd, setAdjustEnd] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustMsg, setAdjustMsg] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  // A rental can't be completed while payment is still owed. An admin can override with a reason.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [completeMsg, setCompleteMsg] = useState('');
  const paymentOutstanding = !!booking.paymentSummary?.receivable
    && booking.paymentSummary?.state !== 'NOT_APPLICABLE'
    && booking.status !== 'cancelled';

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      if (onChanged) await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const deliveryAction = DELIVERY_ACTIONS[booking.deliveryStatus];

  const completeReturn = (opts) => run(async () => {
    setCompleteMsg('');
    try {
      await updateReturnStatus(booking._id, 'COMPLETED', undefined, opts);
      setOverrideOpen(false);
      setOverrideReason('');
    } catch (err) {
      setCompleteMsg(err.response?.data?.msg || 'Unable to complete the rental.');
    }
  });

  const submitAdjust = () => run(async () => {
    setAdjustMsg('');
    try {
      const res = await adjustBookingBilling(booking._id, { endDate: adjustEnd, reason: adjustReason });
      setAdjustMsg(res.applied === false
        ? 'Recorded — the new amount applies once finance approves it.'
        : 'Rental period extended.');
      setAdjustOpen(false);
      setAdjustEnd('');
      setAdjustReason('');
    } catch (err) {
      setAdjustMsg(err.response?.data?.msg || 'Unable to extend the rental period.');
    }
  });

  return (
    <div className="d-flex flex-column gap-1 align-items-start">
      {onViewProduct && (
        <Button size="sm" variant="outline-secondary" onClick={() => onViewProduct(booking.itemId)}>
          View product
        </Button>
      )}

      {needsRefund(booking) && onMarkRefundSent && (
        <Button size="sm" variant="warning" onClick={() => onMarkRefundSent(booking._id)}>
          Mark Refund Sent
        </Button>
      )}

      {booking.paymentSummary?.receivable && booking.status !== 'cancelled' && onRecordPayment && (
        <Button size="sm" variant="outline-success" onClick={() => onRecordPayment(booking)}>
          Mark payment received
        </Button>
      )}
      {booking.payment?.needsReconciliation && (
        <div className="small text-muted">Payment recorded — awaiting finance reconciliation</div>
      )}
      {booking.reconciliationPending && (
        <div className="small text-warning">Rental-period change awaiting finance review</div>
      )}

      {['pending', 'confirmed'].includes(booking.status) && !booking.reconciliationPending && (
        <>
          <Button size="sm" variant="outline-secondary" onClick={() => setAdjustOpen((o) => !o)}>
            Extend rental period
          </Button>
          {adjustOpen && (
            <div className="border rounded p-2 mt-1 w-100">
              <Form.Label className="small mb-1">Extend rental period to</Form.Label>
              <Form.Control type="date" size="sm" className="mb-1" value={adjustEnd} onChange={(e) => setAdjustEnd(e.target.value)} />
              <Form.Control size="sm" className="mb-1" placeholder="Reason (required)" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
              <Button size="sm" disabled={busy || !adjustEnd || !adjustReason} onClick={submitAdjust}>Apply</Button>
              <div className="small text-muted mt-1">The rental is recomputed pro-rata at the listing&apos;s per-day rate. A reduction beyond the auto-approve limit waits for finance.</div>
            </div>
          )}
        </>
      )}
      {adjustMsg && <div className="small text-muted">{adjustMsg}</div>}

      {booking.status === 'cancelled' && onViewCancellation && (
        <Button size="sm" variant="outline-secondary" onClick={() => onViewCancellation(booking)}>
          View cancellation details
        </Button>
      )}

      {booking.status === 'pending' && (
        <div className="d-flex gap-2">
          <Button size="sm" variant="success" disabled={busy} onClick={() => run(() => updateBookingStatus(booking._id, 'confirmed'))}>
            Accept
          </Button>
          <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => setDeclineOpen(true)}>
            Decline
          </Button>
        </div>
      )}

      {booking.status === 'confirmed' && (
        <>
          {deliveryAction && (
            <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => run(() => updateDeliveryStatus(booking._id, deliveryAction.next))}>
              {deliveryAction.label}
            </Button>
          )}
          {booking.deliveryStatus === 'DELIVERED' && booking.returnStatus === 'NOT_DUE' && (
            <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => run(() => updateReturnStatus(booking._id, 'RETURN_SCHEDULED'))}>
              Schedule {RETURN_NOUN}
            </Button>
          )}
          {['RETURN_SCHEDULED', 'PICKED_UP', 'INSPECTION_PENDING'].includes(booking.returnStatus) && (
            <>
              <Button
                size="sm" variant="outline-success" disabled={busy || paymentOutstanding}
                title={paymentOutstanding ? "Payment isn't fully received yet" : undefined}
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm(CONFIRM_RETURN_PROMPT)) completeReturn();
                }}
              >
                Confirm {RETURN_NOUN}
              </Button>
              {paymentOutstanding && (
                <div className="small text-warning">
                  Payment isn&apos;t fully received — the rental can&apos;t be completed.
                  {isAdmin && (
                    <Button size="sm" variant="link" className="p-0 ms-1 align-baseline" onClick={() => setOverrideOpen((o) => !o)}>
                      Override &amp; complete
                    </Button>
                  )}
                </div>
              )}
              {overrideOpen && isAdmin && (
                <div className="border rounded p-2 mt-1 w-100">
                  <Form.Label className="small mb-1">Reason for completing without full payment</Form.Label>
                  <Form.Control size="sm" className="mb-1" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g. customer paying by cheque next week" />
                  <Button size="sm" variant="warning" disabled={busy || !overrideReason.trim()}
                    onClick={() => completeReturn({ override: true, overrideReason: overrideReason.trim() })}>
                    Override &amp; complete
                  </Button>
                </div>
              )}
              {completeMsg && <div className="small text-danger">{completeMsg}</div>}
            </>
          )}
          {/* v9 - "Report Damage" is gone; a damage claim is raised as a support ticket via the
              homepage footer's "Raise a ticket" form. */}
          {['PENDING', 'PICKED_UP', 'IN_TRANSIT'].includes(booking.deliveryStatus) && onAssignDelivery && (
            <Button size="sm" variant="outline-info" onClick={() => onAssignDelivery(booking)}>
              {booking.deliveryAssignment?.assignedTo ? 'Reassign Delivery' : 'Assign Delivery'}
            </Button>
          )}
          {booking.deliveryStatus === 'PENDING' && onCancel && (
            <Button size="sm" variant="outline-danger" onClick={() => onCancel(booking._id)}>
              Cancel Booking
            </Button>
          )}
        </>
      )}
      <ConfirmModal
        show={declineOpen}
        onHide={() => setDeclineOpen(false)}
        title="Decline this booking?"
        message="The request will be cancelled and any refund rules will apply. This cannot be undone from here."
        confirmLabel="Decline"
        confirmVariant="danger"
        busy={busy}
        onConfirm={() => run(async () => {
          await updateBookingStatus(booking._id, 'cancelled');
          setDeclineOpen(false);
        })}
      />
    </div>
  );
}
